import { Button, Form, Input } from 'antd';
import {
  MailOutlined,
  LockOutlined,
  EyeTwoTone,
  EyeInvisibleOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useState, useRef } from 'react';
import axios from 'axios';
import UserService from '../../services/UserService';
import { openNotification } from '../../utils';
import sahaLogo from '../../assets/logo-saha.svg';
import type { Rule } from "antd/es/form";
import { getPostLoginPath } from '../../auth/courseRedirect';
import './Login.scss'; // SCSS'i Register ile ortak kullanıyoruz

type Step = {
  name: string;
  label: string;
  placeholder: string;
  icon: React.ReactNode;
  rules: Rule[];
  isPassword?: boolean;
};

type LoginErrorResponse = {
  errorCode?: string;
};

const isEmailNotVerifiedError = (error: unknown) =>
  axios.isAxiosError<LoginErrorResponse>(error) &&
  error.response?.data?.errorCode === 'email-not-verified';

const getLoginErrorMessage = (error: unknown) => {
  if (!axios.isAxiosError<LoginErrorResponse>(error)) {
    return 'Sunucuya ulaşılamadı. Lütfen bağlantınızı kontrol edip tekrar deneyin.';
  }

  switch (error.response?.data?.errorCode) {
    case 'email-not-verified':
      return 'E-postanızı doğrulayın. Gelen kutunuzu ve spam klasörünü kontrol edin.';
    case 'invalid-username-or-password':
      return 'E-posta adresiniz veya şifreniz hatalı.';
    default:
      return 'Giriş yapılamadı. Lütfen tekrar deneyin.';
  }
};

const steps: Step[] = [
  {
    name: "email",
    label: "E-posta",
    placeholder: "E-posta",
    icon: <MailOutlined />,
    rules: [
      { required: true, message: 'Lütfen e-posta giriniz!' },
      { type: "email", message: 'Geçerli bir e-posta girin!' }
    ],
  },
  {
    name: "password",
    label: "Şifre",
    placeholder: "Şifrenizi girin",
    icon: <LockOutlined />,
    rules: [
      { required: true, message: 'Lütfen şifrenizi girin!' }
    ],
    isPassword: true
  }
];

const Login = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const formRef = useRef<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const postLoginPath = getPostLoginPath(new URLSearchParams(location.search).get('course'));

  const handleNext = async (name: string, value: string) => {
    try {
      await formRef.current.validateFields([name]);
      setFormValues((prev) => ({ ...prev, [name]: value }));
      setCurrentStep(currentStep + 1);
    } catch {
      // Hataları Ant Design gösteriyor.
    }
  };

  const handleBack = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const onFinish = async () => {
    setLoading(true);
    const { email, password } = formValues;
    const apiUrl = process.env.REACT_APP_API_BASE_URL + '/auth/login';
    axios.post(apiUrl, { email, password })
      .then((response) => {
        localStorage.setItem('accessToken', response?.data?.accessToken);
        localStorage.setItem('accessTokenExpiredAt', response?.data?.accessTokenExpiredAt);
        localStorage.setItem('refreshToken', response?.data?.refreshToken);
        localStorage.setItem('refreshTokenExpiredAt', response?.data?.refreshTokenExpiredAt);
        userGet();
      })
      .catch(async (error: unknown) => {
        if (isEmailNotVerifiedError(error)) {
          try {
            await axios.post(process.env.REACT_APP_API_BASE_URL + '/auth/verification-send', { email });
          } catch {
            openNotification('error', 'Onay e-postası gönderilemedi', 'Lütfen onay e-postasını yeniden göndermeyi tekrar deneyin.');
          }
          navigate(`/email-verify-check?email=${encodeURIComponent(email)}`);
          return;
        }

        openNotification('error', 'Giriş yapılamadı', getLoginErrorMessage(error));
        setLoading(false);
      });
  };

  const userGet = () => {
    UserService.getMe()
      .then((response: any) => {
        let role = response.data?.roles[0]?.name;
        localStorage.setItem('firstName', response.data.firstName);
        localStorage.setItem('lastName', response.data.lastName);
        localStorage.setItem('email', response.data.email);
        localStorage.setItem('phoneNumber', response.data.phoneNumber);
        localStorage.setItem('profilePicture', response.data.profilePicture);
        localStorage.setItem('liveLessonCredit', response.data.liveLessonCredit);
        localStorage.setItem('aiChatCredit', response.data.aiChatCredit);
        localStorage.setItem('role', role);
        localStorage.setItem('id', response.data?.id);
        localStorage.setItem('permissions', JSON.stringify(response.data?.roles[0]?.permissions));
        navigate(postLoginPath);
      })
      .catch(() => {
        openNotification('error', 'Başarısız', 'Giriş yapılamadı, Saha Eğitim öğrencisi değilsiniz.');
        setLoading(false);
        localStorage.clear();
      });
  };

  const current = steps[currentStep];

  return (
    <div className="login-page">
      <div className="login-card">
        <div className='login-card-content'>
          <div className="login-logo-row">
            <img src={sahaLogo} alt="Saha Eğitim" style={{ width: 300, maxWidth: "100%", marginBottom: 16, marginTop: 16 }} className="register-logo" />
          </div>
          <h2 style={{ color: '#bd42ab', marginBottom: 24, fontSize: 24 }}>Giriş</h2>
          <Form ref={formRef} layout="vertical" initialValues={formValues}>
            <Form.Item
              name={current.name}
              rules={current.rules}
              initialValue={formValues[current.name]}
            >
              {current.isPassword ? (
                <Input.Password
                  size="large"
                  className="form-input"
                  prefix={current.icon}
                  placeholder={current.placeholder}
                  iconRender={visible => visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />}
                  value={formValues[current.name]}
                  onChange={e =>
                    setFormValues((prev) => ({ ...prev, [current.name]: e.target.value }))
                  }
                  onPressEnter={e =>
                    currentStep === steps.length - 1
                      ? onFinish()
                      : handleNext(current.name, e.currentTarget.value)
                  }
                  autoFocus
                />
              ) : (
                <Input
                  size="large"
                  className="form-input"
                  placeholder={current.placeholder}
                  value={formValues[current.name]}
                  onChange={e =>
                    setFormValues((prev) => ({ ...prev, [current.name]: e.target.value }))
                  }
                  onPressEnter={e =>
                    handleNext(current.name, e.currentTarget.value)
                  }
                  autoFocus
                />
              )}
            </Form.Item>
            <div className="register-nav-btns">
              {currentStep > 0 && (
                <Button
                  size="large"
                  icon={<ArrowLeftOutlined />}
                  onClick={handleBack}
                  className="register-back-btn"
                />
              )}
              {currentStep < steps.length - 1 ? (
                <Button
                  onClick={() => handleNext(current.name, formValues[current.name])}
                  size="large"
                  type="primary"
                >Sonraki</Button>
              ) : (
                <Button
                  loading={loading}
                  type="primary"
                  size="large"
                  onClick={onFinish}
                  block
                >
                  Giriş Yap
                </Button>
              )}
            </div>
          </Form>
          <div className="login-footer-links">
            <Link to="/forgot-password">Şifreni mi unuttun?</Link>
            <p>
              Hesabın yok mu? <Link to="/kaydol">Kaydol</Link>
            </p>
          </div>
          <footer className="login-footer">
            <p>sahadabasari.com © {new Date().getFullYear()}</p>
          </footer>
        </div>

      </div>
    </div>
  );
};

export default Login;
